/**
 * Clerk authentication boundary (APP-001 §7).
 *
 * Protected application routes must:
 *   1. wait for Clerk initialization;
 *   2. require a signed-in Clerk session;
 *   3. obtain a session token (handled in the API client via the token
 *      provider installed in providers.tsx);
 *   4. call the backend account/bootstrap endpoint (AccountBootstrap);
 *   5. render the dashboard only after backend account resolution succeeds.
 *
 * `AuthBoundary` enforces steps 1–2. `AccountBootstrap` (rendered as its
 * child via the dashboard layout) enforces steps 3–5. A signed-out visitor is
 * redirected to /sign-in with the attempted path preserved as a return path.
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { parseReturnIntent } from './returnPath';
import { LoadingState } from '@/components/feedback/LoadingState';

export function AuthBoundary({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  // Step 1: wait for Clerk initialization. Protected routes never render while
  // authentication is unresolved (APP-001 §33 invariant 13).
  if (!isLoaded) {
    return <LoadingState label="Loading session…" />;
  }

  // Step 2: signed-out users cannot access application routes (§33 invariant 14).
  if (!isSignedIn) {
    const params = new URLSearchParams({ redirect: location.pathname + location.search });
    return <Navigate to={`/sign-in?${params.toString()}`} replace />;
  }

  // Steps 3–5 are delegated to AccountBootstrap inside the dashboard layout.
  return <>{children}</>;
}

/**
 * Convenience wrapper: redirects signed-in users away from auth-only routes
 * (sign-in/sign-up) toward the dashboard or a preserved return path.
 */
export function RedirectIfSignedIn({ to = '/dashboard' }: { to?: string }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  if (!isLoaded) return <LoadingState label="Loading session…" />;
  if (!isSignedIn) return null;
  const intent = parseReturnIntent(location.search);
  return <Navigate to={intent.returnPath ?? to} replace />;
}
